import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { useUser } from '@work4all/data';

import {
  checkUserRight,
  UserRights,
} from '@work4all/models/lib/additionalEnums/UserRights';

import { TimeTracker } from '../use-time-tracker';
import { checkIsBeforeEarliestCheckinTime } from '../utils/checkIsBeforeEarliestCheckingTime';

export function useWorkTimeCheckin(tracker: TimeTracker) {
  const { t } = useTranslation();
  const user = useUser();
  const dialogs = useDialogs();

  const [dialog, setDialog] = useState<{
    open: boolean;
    type: 'start' | 'stop';
  }>({
    open: false,
    type: 'start',
  });

  const earliestCheckinTime = tracker.result?.earliestCheckinTime;
  const isBeforeEarliestCheckinTime = useMemo(
    () => checkIsBeforeEarliestCheckinTime(earliestCheckinTime),
    [earliestCheckinTime]
  );

  const handleStartBeforeEarliestTime = () => {
    if (earliestCheckinTime) {
      tracker.start(earliestCheckinTime.toJSDate());
    }
  };

  const determineCheckinCheckoutTime = (type: 'start' | 'stop') => () => {
    const canModifyExactTimes = checkUserRight(
      UserRights.KommtGehtZeitenAendernEigene,
      user.benutzerRechte
    );

    if (canModifyExactTimes) {
      if (type === 'start' && isBeforeEarliestCheckinTime) {
        dialogs.alert({
          title: t('TIME_TRACKER.EARLIEST_CHECKIN_TITLE'),
          description: t('TIME_TRACKER.EARLIEST_CHECKIN_MESSAGE', {
            time: earliestCheckinTime?.toLocaleString(DateTime.TIME_SIMPLE),
          }),
          closeLabel: t('COMMON.OK'),
          onClose: handleStartBeforeEarliestTime,
        });
        return;
      } else if (type === 'stop' && isBeforeEarliestCheckinTime) {
        tracker.stop(new Date());
      } else {
        setDialog({
          open: true,
          type,
        });
      }
    } else {
      tracker[type](new Date());
    }
  };

  const closeDialog = () => {
    setDialog((state) => ({ ...state, open: false }));
  };

  return {
    dialog,
    setDialog,
    closeDialog,
    earliestCheckinTime,
    determineCheckinCheckoutTime,
    handleStartBeforeEarliestTime,
  };
}
